/**
 * DI-3-C — the per-set phase machine of the runner (story §2.1).
 *
 *   decided → creating → created → syncing → synced → completing → running
 *
 * `running` means the SERVER runs the set (matching, awaiting a log …); the
 * browser's part of M3a ends there (DI-3-F adds the upload phases after
 * `synced`, DI-3-G `addToSet`). A step that fails leaves the set on the phase
 * it was in with `error` set; Retry repeats that step only.
 *
 * `syncing` is the ONE phase the hashes gate: a created set waits in
 * `created` until every row of its membership is hashed.
 */

export const SET_PHASES = [
  "decided",
  "creating",
  "created",
  "syncing",
  "synced",
  "completing",
  "running",
] as const;
export type SetPhase = (typeof SET_PHASES)[number];

/** The step that runs FROM a phase (the step Retry repeats). */
export type SetStep = "create" | "sync" | "complete";

export interface PhaseTime {
  startedAt: string;
  finishedAt?: string;
}

export type PhaseTimes = Partial<Record<SetPhase, PhaseTime>>;

export interface SetError {
  step: SetStep;
  message: string;
  status: number | null;
  reason: string | null;
  /** 409 `instruction_already_has_acquisition` — the instruction's set. */
  heldAcquisitionId?: string;
  /**
   * DI-3-S68 F6 — `unreadable_files` only: a re-read found the drive gone
   * (not one of the files could be read) — "Waiting for the drive".
   */
  drive?: "gone";
}

/** The step a set in `phase` needs next, or null when the browser is done with it. */
export function nextStep(phase: SetPhase): SetStep | null {
  switch (phase) {
    case "decided":
    case "creating":
      return "create";
    case "created":
    case "syncing":
      return "sync";
    case "synced":
    case "completing":
      return "complete";
    case "running":
      return null;
  }
}

/** Did the set get past `phase`? (`created` is past `creating`, …) */
export function reached(phase: SetPhase, target: SetPhase): boolean {
  return SET_PHASES.indexOf(phase) >= SET_PHASES.indexOf(target);
}

/** Record `phase` as entered at `now` and close the previous one. */
export function enter(
  times: PhaseTimes,
  from: SetPhase,
  to: SetPhase,
  now: Date,
): PhaseTimes {
  const at = now.toISOString();
  const prev = times[from];
  return {
    ...times,
    ...(prev && !prev.finishedAt && from !== to
      ? { [from]: { ...prev, finishedAt: at } }
      : {}),
    [to]: { startedAt: at },
  };
}

/** Words for the phase on the handover rows. */
export const PHASE_LABEL: Readonly<Record<SetPhase, string>> = {
  decided: "waiting to start",
  creating: "creating the data set",
  created: "data set created · waiting for the hashes",
  syncing: "sending the image metadata",
  synced: "metadata sent",
  completing: "handing over to the server",
  running: "processing on the server",
};
